import { InfoOutlineIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Radio,
  RadioGroup,
  Tooltip,
} from '@chakra-ui/react';
import { TextInput, usePaginationSessionStorage, useTabStorage } from '@laxmimanogna/code-quick-components';
import React, { useContext, useState, useEffect, useMemo } from 'react';
import Select from 'react-select'
import AppColors from '../../constants/AppColors';
import { FONT_FAMILY } from '../../constants/Theme';
import { withMultiProviders } from '../../hoc/withProvider';
import useCQMediaQuery from '../../hooks/useCQMediaQuery';
import MyChartProvider, {
  MyChartContext,
} from '../../providers/MyChartProvider';
import AllCharts from './components/tables/AllCharts';
import ArchivedCharts from './components/tables/ArchivedCharts';
import RebuttedCharts from './components/tables/RebuttedCharts';
import AuditSheetProvider, { AuditSheetContext } from '../../providers/AuditSheetProvider';
import NewChartsTable from './components/tables/NewChartsTable';
import { MY_CHARTS_FILTER_OPTIONS, MY_CHARTS_FILTER_OPTIONS_TO_ACCOUNT_QUERY_MAP } from '../../constants/constants';
import { MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME } from './constants';


const DropdownComponent = ({
  options,
  setSelected,
  selected,
  placeholder = "Select Options",
  isDisabled=false,
  disabledText = "Dropdown disabled",
  isLoading=false,
  isClearable=true,
  isSearchable=true
}) => {
  const handleChange = (option) => {
    setSelected(option);
  }

  return (
    <div
    style={{
      flex:1,
    }}>
      <Select 
      options={options}
      onChange={handleChange}
      isClearable={isClearable}
      placeholder={placeholder}
      isDisabled={isDisabled}
      value={selected}
      isLoading={isLoading}
      isSearchable={isSearchable}
      />
    
    </div> 
  )
}

const MyChartsContainerV2 = props => {
  const myChartsTab = [
    {
      title: 'ALL',
      header: 'Audit Uploads',
      component: NewChartsTable,
    },
  ];
  const {
    getPaginationSession
  } = usePaginationSessionStorage();
  const myChartContext = useContext(MyChartContext);
  const auditSheetContext = useContext(AuditSheetContext);
  const { 
    handleApplyFilter, 
    filterParams, 
    getAllCharts, 
    getArchivedCharts,
    chartStatusFilters,
    setChartStatusFilters,
    isAllChartLoading
  } =
    myChartContext;

  const tabStorage = useTabStorage();
  const [selectedTab, setSelectedTab] = useState(tabStorage.getTab() || 0);
  const [onSearchText, setSearchText] = useState(getPaginationSession(MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME)?.search || '');

  useEffect(() => {tabStorage.setTab(selectedTab)}, [selectedTab]);

  const dropdownOptions = useMemo(
    () => MY_CHARTS_FILTER_OPTIONS.map(val => ({ value: val, label: val })),
    [MY_CHARTS_FILTER_OPTIONS]
  );

  const [selectedFilter, setSelectedFilter] = useState(() => ({
    value: MY_CHARTS_FILTER_OPTIONS[tabStorage.getTab() || 0],
    label: MY_CHARTS_FILTER_OPTIONS[tabStorage.getTab() || 0]
  }));

  useEffect(() => {
    if (!selectedFilter.value) {
      return;
    }
    tabStorage.setTab(MY_CHARTS_FILTER_OPTIONS.indexOf(selectedFilter.value))
  }, [selectedFilter]);

  const marginHorizontal = useCQMediaQuery({
    lg: '12%',
    md: '6%',
    sm: '2%',
  });

  const onSearchAction = () => {
    handleApplyFilter({ ...filterParams, searchText: onSearchText });
  };

  const onFilterChange = e => {
    if (e) {
      getAllCharts.setParams({ page: 1 });
      getArchivedCharts.setParams({ page: 1 });
      setSearchText(e.target.value);
    } else {
      setSearchText('');
    }
  };

  const onOpenNotes = (obj, batchId, tabType = undefined) => {
    if (obj && Object.keys(obj).length) {
      if (obj.all) {
        delete auditSheetContext.commentFilters.sheet_name
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          page: 1,
          tabType: tabType
        })
      } else if (obj.current) {
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          sheet_name: "Sheet1",
          page: 1,
          tabType: tabType
        })
      }
    } else {
      auditSheetContext.setCommentFilters({
        ...auditSheetContext.commentFilters,
        batchId,
        sheet_name: "Sheet1",
        reverse: true,
        page: 1,
        tabType: tabType
      })
    }

  }

  return (
    <>
      <Box mx={marginHorizontal}>
        <HStack spacing={2} ><Heading my={5} as="h5" fontSize="24px">
          My Charts
        </Heading>
          <Tooltip p={3} bg="whiteAlpha.900"
            color="black"
            borderRadius="md"
            border="1px solid #43dde0" label={
              <Box>
                <>
                  <p>
                    <b>My Charts</b> tab displays only the charts that you have personally uploaded. This helps you manage and review your individual contributions separately.
                  </p>
                </>
              </Box>
            } placement="top-end">
            <Icon as={InfoOutlineIcon} w={4} h={4} cursor="pointer" _hover={{ color: AppColors.secondary, transition: 'color 0.3s ease-in-out' }} color={AppColors.primary} />
          </Tooltip></HStack>
        <Box
          shadow={'md'}
          bg="white"
          p={4}
          w="100%"
          style={{ borderRadius: '12px' }}
        >
          <HStack w={'100%'} justifyContent={'space-between'} pb={3} borderBottom="1px solid" borderColor="gray.100">
            <Box width={"30%"}>
            <DropdownComponent
                    options={dropdownOptions}
                    selected={selectedFilter}
                    setSelected={option => {
                      setSelectedFilter(option);
                      setChartStatusFilters(MY_CHARTS_FILTER_OPTIONS_TO_ACCOUNT_QUERY_MAP[option.value])
                      getAllCharts.loadPageByFilterParams(MY_CHARTS_PAGE_NAME, MY_CHARTS_TABLE_NAME, {chart_statuses: MY_CHARTS_FILTER_OPTIONS_TO_ACCOUNT_QUERY_MAP[option.value]})
                    }}
                    placeholder=''
                    isClearable={false}
                    isSearchable={false}
                    isDisabled={isAllChartLoading}
                    isLoading={isAllChartLoading}
                    />
                    </Box>
            <Box>
              <TextInput
                isDisabled={isAllChartLoading}
                borderRadius={'md'}
                value={onSearchText}
                onChange={e => onFilterChange(e)}
                onKeyDown={e => {
                  if (e.keyCode === 13) {
                    onSearchAction();
                  }
                }}
                mb="2"
                rightIconProps={{ zIndex: 0 }}
                placeholder="Search"
                rightIcon={
                  <SearchIcon
                    onClick={onSearchAction}
                    color={AppColors.gray}
                  />
                }
              />
            </Box>
          </HStack>

          <Box pt={3}>
            {myChartsTab.map((aa, index) => (
               (
                <aa.component 
                key={`my-charts-panel-${index}`} 
                onOpenNotes={onOpenNotes}
                PAGE_NAME={MY_CHARTS_PAGE_NAME}
                TABLE_NAME={MY_CHARTS_TABLE_NAME}
                 />
              )
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default withMultiProviders([MyChartProvider, AuditSheetProvider], MyChartsContainerV2);




// const DashboardContainerV2 = props => {
//   // states
//   const {
//     getPaginationSession
//   } = usePaginationSessionStorage();
//   const [onSearchText, setSearchText] = useState(getPaginationSession(DASHBOARD_PAGE_NAME, DASHBOARD_TABLE_NAME)?.search || '');
//   const tabStorage = useTabStorage();
//   const [selectedTab, setSelectedTab] = useState(0);
//   const auditSheetContext = useContext(AuditSheetContext)

//   useEffect(() => {tabStorage.setTab(selectedTab)}, [selectedTab]);

//   // other hooks
//   const marginHorizontal = useCQMediaQuery({
//     lg: '12%',
//     md: '6%',
//     sm: '2%',
//   });

//   // context
//   const myAuditContext = useContext(MyAuditContext);
//   const {
//     auditUrgentListCount,
//     getRecentAudits,
//     handleApplyFilter,
//     filterParams,
//     getAllCharts,
//     chartStatusFilters,
//     setChartStatusFilters,
//     isAllChartsLoading
//   } = myAuditContext;

//   const onSearchAction = () => {
//     handleApplyFilter({ ...filterParams, searchText: onSearchText });
//   };

//   const onFilterChange = e => {
//     if (e) {
//       setSearchText(e.target.value);
//     } else {
//       setSearchText('');
//     }
//   };

//   const auditorPortalTabs = [
//     {
//       title: 'Charts',
//       header: 'Charts',
//       component: NewAuditTable,
//     },
//   ];

//   // life-cycle hooks
//   useEffect(() => {
//     getRecentAudits();
//   }, []);

//   const dropdownOptions = useMemo(
//     () => FILTER_OPTIONS.map(val => ({ value: val, label: val })),
//     [FILTER_OPTIONS]
//   );

//   const [selectedFilter, setSelectedFilter] = useState(() => ({
//     value: FILTER_OPTIONS[tabStorage.getTab() || 0],
//     label: FILTER_OPTIONS[tabStorage.getTab() || 0]
//   }));

//   useEffect(() => {
//     if (!selectedFilter.value) {
//       return;
//     }
//     tabStorage.setTab(FILTER_OPTIONS.indexOf(selectedFilter.value))
//   }, [selectedFilter]);

//   const onOpenNotes = (obj, batchId, tabType = undefined) => {
//     if (obj && Object.keys(obj).length) {
//       if (obj.all) {
//         delete auditSheetContext.commentFilters.sheet_name
//         auditSheetContext.setCommentFilters({
//           ...auditSheetContext.commentFilters,
//           page: 1,
//           tabType: tabType
//         })
//       } else if (obj.current) {
//         auditSheetContext.setCommentFilters({
//           ...auditSheetContext.commentFilters,
//           sheet_name: "Sheet1",
//           page: 1,
//           tabType: tabType
//         })
//       }
//     } else {
//       auditSheetContext.setCommentFilters({
//         ...auditSheetContext.commentFilters,
//         batchId,
//         sheet_name: "Sheet1",
//         reverse: true,
//         page: 1,
//         tabType: tabType
//       })
//     }
//   }


//   return (
//     <Box>
//       <Box py={5} height={250} bg={'white'} px={marginHorizontal}>
//         <Heading
//           fontSize={'24px'}
//           fontFamily={FONT_FAMILY.baiJamurjee}
//           fontWeight="bold"
//         >
//           Recent Audits
//         </Heading>
//         <RecentAudits />
//       </Box>
//       <CQContainer>
//         <Heading my={5} fontSize={'xl'}>
//           {auditorPortalTabs[selectedTab].header}
//         </Heading>
//         <Box
//           shadow={'md'}
//           bg="white"
//           p={4}
//           w="100%"
//           style={{ borderRadius: '12px' }}
//         >
//           <Tabs
//             fontFamily={FONT_FAMILY.baiJamurjee}
//             onChange={setSelectedTab}
//             index={selectedTab}
//           >
//             <TabList>
//               <HStack w={'100%'} justifyContent={'space-between'}>
//                 {/* filtersss */}
//                   <Box width={"30%"}>
//                     <DropdownComponent
//                     options={dropdownOptions}
//                     selected={selectedFilter}
//                     setSelected={option => {
//                       setSelectedFilter(option);
//                       setChartStatusFilters(FILTER_OPTIONS_TO_CHART_STATUS_MAP[option.value])
//                       getAllCharts.loadPageByFilterParams(DASHBOARD_PAGE_NAME, DASHBOARD_TABLE_NAME, {chart_statuses: FILTER_OPTIONS_TO_CHART_STATUS_MAP[option.value]})
//                     }}
//                     placeholder=''
//                     isClearable={false}
//                     isSearchable={false}
//                     isDisabled={isAllChartsLoading}
//                     isLoading={isAllChartsLoading}
//                     />
//                   </Box>
//                 <Box>
//                   <TextInput
//                     borderRadius={'md'}
//                     value={onSearchText}
//                     onChange={e => onFilterChange(e)}
//                     onKeyDown={e => {
//                       if (e.keyCode === 13) {
//                         onSearchAction();
//                       }
//                     }}
//                     mb="2"
//                     rightIconProps={{ zIndex: 0 }}
//                     placeholder="Search"
//                     rightIcon={<SearchIcon onClick={onSearchAction} />}
//                     isDisabled={isAllChartsLoading}
//                   />
//                 </Box>
//               </HStack>
//             </TabList>

//             <TabPanels>
//               {auditorPortalTabs.map((apt, i) => {
//                 return (
//                   <TabPanel p={0} key={`assigned-audit-panel-${i}`}>
//                     <apt.component 
//                     key={`my-charts-panel-${i}`} 
//                     onOpenNotes={onOpenNotes} 
//                     PAGE_NAME={DASHBOARD_PAGE_NAME} 
//                     TABLE_NAME={DASHBOARD_TABLE_NAME}
//                     />
//                   </TabPanel>
//                 );
//               })}
//             </TabPanels>
//           </Tabs>
//         </Box>
//       </CQContainer>
//     </Box>
//   );
// };
